import {Stack, Typography} from '@mui/material';
import {ErrorState, Snackbar} from 'presentation/components';
import {Drawer} from 'presentation/components/Drawer';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {ExamResultsTaggingEnum} from 'main/analytics/gaTagsEnum';

import {BeneficiaryInfo} from '../BeneficiaryInfo';
import {CustomSnackbar} from '../CustomSnackbar';
import {ExamDrawerLoading} from '../ExamDrawerLoading';
import {ExamDrawerProps} from './types';
import {ExamReportItem} from '../ExamReportItem';
import {useExamRequestReports} from 'presentation/modules/examResults/hooks/useExamRequestReports';

const {title, subtitle, downloadError, downloadInfo, copiedLink, errorTitle, errorDescription} =
	translations['pt-br'].privateFlow.examResults.drawer;

export const ExamDrawer = ({isWaitingResult}: ExamDrawerProps) => {
	const {
		closeDrawer,
		examReports,
		examRequestData,
		isMobile,
		isError,
		isLoading,
		refetch,
		showPlural,
		openPdf,
		shareButtonLabel,
		shareButtonIsLoading,
		secondButtonLabel,
		secondButtonAction,
		secondButtonIsLoading,
		handleCloseSnackbar,
		isInfoAlert,
		isErrorAlert,
		showSnackbar,
		showContentAndActions,
		hideShareButton,
	} = useExamRequestReports(!!isWaitingResult);

	return (
		<Fragment>
			<Drawer.Root
				onClose={closeDrawer}
				onOpen={() => null}
				open={!!examRequestData}
				width={'645px'}
				customMaxHeight={'90%'}
				customMaxWidth={'90%'}>
				<Drawer.Header
					closeBtnDataTagId={ExamResultsTaggingEnum.CLICK_FECHAR_DRAWER_RESULTADO_EXAME}
					title={title}
					onClose={closeDrawer}
					containerPadding={isMobile ? '0 8px 16px 16px' : '0 16px 16px 24px'}
				/>
				<Drawer.Content>
					{isLoading ? <ExamDrawerLoading /> : null}
					{isError ? (
						<ErrorState
							title={errorTitle}
							description={errorDescription}
							handleRefresh={refetch}
						/>
					) : null}

					{showContentAndActions ? (
						<Fragment>
							<BeneficiaryInfo
								orderNumber={examRequestData?.nuPedido.toString() ?? ''}
								location={examRequestData?.dsPrestadorJuridico ?? ''}
							/>
							<Stack py={2}>
								<Typography variant="subtitle2" color={'primary'}>
									{subtitle}
									{showPlural}
								</Typography>
								<Stack gap={2}>
									{examReports?.laudos.map((exam, index) => (
										<ExamReportItem
											key={index}
											exam={exam}
											isFirst={index === 0}
										/>
									))}
								</Stack>
							</Stack>
						</Fragment>
					) : null}
				</Drawer.Content>
				{showContentAndActions ? (
					<Drawer.Footer
						canStacks
						hideFirstButton={hideShareButton}
						firstButtonTitle={shareButtonLabel}
						firstButtonOnClick={() => openPdf(true)}
						firstButtonIsLoading={shareButtonIsLoading}
						firstButtonDisabled={shareButtonIsLoading}
						firstButtonDataTagId={
							isWaitingResult
								? ExamResultsTaggingEnum.CLICK_COMPARTILHAR_RESULTADO_DE_EXAMES_AGUARDANDO_LIBERACAO
								: ExamResultsTaggingEnum.CLICK_COMPARTILHAR_RESULTADO_DE_EXAMES_LIBERADOS
						}
						secondButtonTitle={secondButtonLabel}
						secondButtonOnClick={secondButtonAction}
						secondButtonIsLoading={secondButtonIsLoading}
						secondButtonDisabled={secondButtonIsLoading}
						secondButtonDataTagId={
							isWaitingResult || hideShareButton
								? ExamResultsTaggingEnum.CLICK_FECHAR_DRAWER_EXAMES_AGUARDANDO_LIBERACAO
								: ExamResultsTaggingEnum.CLICK_BAIXAR_LAUDO_EXAME_LIBERADOS
						}
					/>
				) : null}
			</Drawer.Root>
			<CustomSnackbar
				title={downloadInfo.title}
				description={downloadInfo.description}
				snackProps={{open: isInfoAlert}}
				alertProps={{severity: 'info'}}
			/>
			<CustomSnackbar
				title={downloadError.title}
				description={downloadError.description}
				snackProps={{open: isErrorAlert}}
				alertProps={{severity: 'error'}}
				dataTagId={ExamResultsTaggingEnum.VISUALIZOU_SNACKBAR_ERRO_AO_BAIXAR_LAUDO}
			/>
			<Snackbar onClose={handleCloseSnackbar} message={copiedLink} open={showSnackbar} />
		</Fragment>
	);
};
